import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  APPEARANCE_FAMILY_OPTIONS,
  PR_VARIANT_OPTIONS,
  SW_VARIANT_OPTIONS,
  ST_VARIANT_OPTIONS,
  HONEYCOMB_VARIANT_OPTIONS,
  appearanceFamilyOf,
  prViewportFrameInset,
} from '../theme/appAppearance';
import { useAppAppearance } from '../theme/AppAppearanceContext';
import { hologramSurfaceSx } from '../theme/hologramChrome';
import { SettingsCollapsibleGroup } from './SettingsCollapsibleGroup';
import { playPrSfx } from '../audio/pacificRimSfx';
import { useI18n } from '../hooks/useI18n';

const toggleGroupSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  '& .MuiToggleButtonGroup-grouped': {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1.5,
    m: 0,
    justifyContent: 'flex-start',
    px: 1.25,
    py: 0.75,
    textTransform: 'none',
  },
};

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onLogout: () => void,
 *   onAppearanceChange?: (id: string) => void,
 * }} props
 */
export function SettingsDialog({
  open,
  onClose,
  onLogout,
  onAppearanceChange,
}) {
  const { t } = useI18n();
  const theme = useTheme();
  const { appearance, setAppearance, chromeStyle, setChromeStyle, hologram } =
    useAppAppearance();
  const family = appearanceFamilyOf(appearance);
  const s = t.settings;
  const frameInset = hologram ? prViewportFrameInset(appearance) : 0;
  const accent = theme.palette.primary.main;

  const variantOptions =
    family === 'starwars'
      ? SW_VARIANT_OPTIONS
      : family === 'startrek'
        ? ST_VARIANT_OPTIONS
        : family === 'pacificrim'
          ? PR_VARIANT_OPTIONS
          : null;

  const variantAria =
    family === 'starwars'
      ? s.designVariantAriaSw
      : family === 'startrek'
        ? s.designVariantAriaSt
        : family === 'honeycomb'
          ? s.designVariantAriaHoneycomb
          : s.designVariantAriaPr;

  const frameWidth =
    frameInset > 0 ? `calc(100vw - ${frameInset * 2}px)` : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: frameWidth || '100%',
            maxWidth: frameWidth || {
              xs: 'calc(100% - 16px)',
              sm: 600,
              md: 1200,
            },
            m: frameInset > 0 ? `${frameInset}px` : { xs: 1, sm: 2 },
            ...(hologram
              ? {
                  ...hologramSurfaceSx(accent),
                  color: 'rgba(232, 246, 255, 0.96)',
                }
              : {}),
          },
        },
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: frameInset > 0 ? 'stretch' : 'center',
        },
      }}
    >
      <DialogTitle>{s.title}</DialogTitle>
      <DialogContent dividers>
        <SettingsCollapsibleGroup title="Design">
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1, fontWeight: 600 }}
          >
            {s.design}
          </Typography>
          <ToggleButtonGroup
            exclusive
            orientation="vertical"
            fullWidth
            size="small"
            value={family}
            onChange={(_, next) => {
              if (!next) return;
              const opt = APPEARANCE_FAMILY_OPTIONS.find((o) => o.id === next);
              if (opt) {
                setAppearance(opt.defaultAppearance);
                onAppearanceChange?.(opt.defaultAppearance);
              }
            }}
            aria-label={s.designAria}
            sx={toggleGroupSx}
          >
            {APPEARANCE_FAMILY_OPTIONS.map((opt) => (
              <ToggleButton key={opt.id} value={opt.id}>
                {s[opt.labelKey]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {family === 'honeycomb' && (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1.5, mb: 1, fontWeight: 600 }}
              >
                {s.designVariant}
              </Typography>
              <ToggleButtonGroup
                exclusive
                orientation="vertical"
                fullWidth
                size="small"
                value={appearance}
                onChange={(_, next) => {
                  if (!next) return;
                  setAppearance(next);
                  onAppearanceChange?.(next);
                }}
                aria-label={s.designVariantAriaHoneycomb}
                sx={toggleGroupSx}
              >
                {HONEYCOMB_VARIANT_OPTIONS.map((opt) => (
                  <ToggleButton key={opt.id} value={opt.id}>
                    {s[opt.labelKey]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </>
          )}

          {variantOptions && (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1.5, mb: 1, fontWeight: 600 }}
              >
                {s.designVariant}
              </Typography>
              <ToggleButtonGroup
                exclusive
                orientation="vertical"
                fullWidth
                size="small"
                value={appearance}
                onChange={(_, next) => {
                  if (next) {
                    setAppearance(next);
                    onAppearanceChange?.(next);
                  }
                }}
                aria-label={variantAria}
                sx={toggleGroupSx}
              >
                {variantOptions.map((opt) => (
                  <ToggleButton key={opt.id} value={opt.id}>
                    {s[opt.labelKey]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </>
          )}
        </SettingsCollapsibleGroup>

        {family === 'pacificrim' && (
          <SettingsCollapsibleGroup title="Darstellung">
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1, fontWeight: 600 }}
            >
              {s.chromeStyle}
            </Typography>
            <ToggleButtonGroup
              exclusive
              orientation="vertical"
              fullWidth
              size="small"
              value={chromeStyle}
              onChange={(_, next) => {
                if (!next) return;
                setChromeStyle(next);
                playPrSfx('select');
              }}
              aria-label={s.chromeStyleAria}
              sx={toggleGroupSx}
            >
              <ToggleButton value="hologram">
                {s.chromeStyleHologram}
              </ToggleButton>
              <ToggleButton value="classic">
                {s.chromeStyleClassic}
              </ToggleButton>
            </ToggleButtonGroup>
          </SettingsCollapsibleGroup>
        )}

        <Divider sx={{ my: 2 }} />
        <Stack spacing={1}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={() => {
              onClose();
              onLogout();
            }}
            fullWidth
            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
          >
            {t.common.logout}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{s.close}</Button>
      </DialogActions>
    </Dialog>
  );
}
